<template>
  <div
    class="product"
    :class="{ 'product--has-final-sale': shouldShowFinalSale }"
  >
    <div class="product__image-wrapper">
      <Picture :images="firstImage" />
    </div>
    <div class="product__details">
      <div class="product__title">{{ productTitle }}</div>
      <div
        v-for="variant in merchandiseSelectedOptions"
        :key="variant.name"
        class="product__variant"
      >
        <span class="variant-title">{{ variant.value }}</span>
      </div>
      <div v-if="sellingPlanName" class="product__sellingPlan-label">
        {{ sellingPlanName }}
      </div>
      <div
        class="product__price"
        :class="{ 'product__price--onsale': isOnSale }"
      >
        <span class="product__price-original">{{
          formattedOriginalPrice
        }}</span>
        <span v-if="isOnSale" class="product__price-sale">
          {{ formattedPrice }}
          <span class="product__price-percent"
            >({{ percentageDiscount }}% off)</span
          >
        </span>
      </div>
      <p v-if="estimatedDeliveryLabel" class="product__estimated-delivery">
        {{ estimatedDeliveryLabel }}
      </p>
      <div v-if="shouldShowFinalSale" class="product__final-sale">
        <button
          type="button"
          class="final-sale-trigger"
          @click="openFinalSaleModal"
        >
          <span class="final-sale-trigger__label">Final Sale</span>
          <img
            src="@/assets/images/question-mark.svg"
            alt=""
            class="question-mark-icon"
          />
        </button>
      </div>
      <div v-if="shipsFromUkWarehouse" class="product__uk-warehouse">
        <button
          type="button"
          class="uk-warehouse-trigger"
          @click="openUkWarehouseModal"
        >
          <span class="uk-warehouse-trigger__label">UK Delivery</span>
          <img
            src="@/assets/images/question-mark.svg"
            alt=""
            class="question-mark-icon"
          />
        </button>
      </div>
      <div class="product__quantity">
        <NumberStepper
          :count="quantity"
          :max="maxQuantity"
          @change="onQuantityChange"
        />
        <button
          class="remove-product-button"
          type="button"
          @click="$emit('removeLine')"
        >
          Remove
        </button>
      </div>
    </div>
  </div>

  <SlideIn
    :open="finalSaleModalOpen"
    heading="Final Sale"
    @close="closeFinalSaleModal"
  >
    <template #content>
      <div class="final-sale-modal-content">
        <p>
          Final Sale Items: These are not eligible for return, except where
          required by law. For more details, please refer to our FAQs.
        </p>
      </div>
    </template>
  </SlideIn>

  <SlideIn
    :open="ukWarehouseModalOpen"
    heading="UK Delivery"
    @close="closeUkWarehouseModal"
  >
    <template #content>
      <div class="final-sale-modal-content">
        <p>{{ ukWarehouseDeliveryMessage }}</p>
      </div>
    </template>
  </SlideIn>
</template>
<script>
import { unwrapNodes } from "@/helpers/graphql";
import { getEstimatedDeliveryLabel } from "@/helpers/cartLineAttributes";
import Picture from "@/components/Picture.vue";
import NumberStepper from "@/components/NumberStepper";
import SlideIn from "@/components/SlideIn.vue";
import { mapGetters } from "vuex";
import { getFeatureToggles } from "@/helpers/featureToggles.js";
import { hasFinalSaleTag } from "@/helpers/finalSale.js";
import {
  hasUkWarehouseTag,
  UK_WAREHOUSE_DELIVERY_MESSAGE,
} from "@/helpers/ukWarehouse.js";

export default {
  name: "CartLine",
  components: {
    NumberStepper,
    Picture,
    SlideIn,
  },
  props: {
    line: {
      type: Object,
      required: true,
    },
  },
  emits: ["removeLine", "updateLine"],
  data() {
    return {
      finalSaleModalOpen: false,
      ukWarehouseModalOpen: false,
    };
  },
  computed: {
    ...mapGetters("app", ["currencyCode"]),
    productTitle() {
      return this.line.merchandise.product.title;
    },
    merchandiseSelectedOptions() {
      const options = this.line.merchandise.selectedOptions || [];

      // Gift cards (and other single-variant products) use Shopify's
      // placeholder "Default Title" — hide it from the bag line.
      return options.filter((option) => {
        const value = option?.value?.trim?.() || "";
        return value && value.toLowerCase() !== "default title";
      });
    },
    firstImage() {
      const images = unwrapNodes(this.line.merchandise.product.images.edges);
      return images ? images[0] : null;
    },
    quantity() {
      return this.line.quantity;
    },
    maxQuantity() {
      return this.line.merchandise.quantityAvailable;
    },
    isOnSale() {
      const merchandise = this.line?.merchandise;
      const compareAt = merchandise?.compareAtPriceV2;
      if (!compareAt || !compareAt.amount) return false;
      const priceV2 = merchandise?.priceV2;
      if (!priceV2?.amount) return false;
      const current = parseFloat(priceV2.amount);
      const compare = parseFloat(compareAt.amount);
      return compare > current;
    },
    formattedPrice() {
      const priceV2 = this.line?.merchandise?.priceV2;
      if (!priceV2) return "";
      const { amount, currencyCode } = priceV2;
      return this.$filters.toCurrency(amount, currencyCode, 0);
    },
    formattedOriginalPrice() {
      if (this.isOnSale) {
        const compareAt = this.line?.merchandise?.compareAtPriceV2;
        if (!compareAt) return this.formattedPrice;
        const { amount, currencyCode } = compareAt;
        return this.$filters.toCurrency(amount, currencyCode, 0);
      }
      return this.formattedPrice;
    },
    percentageDiscount() {
      if (!this.isOnSale) return 0;
      const compareAt = this.line?.merchandise?.compareAtPriceV2;
      const priceV2 = this.line?.merchandise?.priceV2;
      if (!compareAt?.amount || !priceV2?.amount) return 0;
      const original = parseFloat(compareAt.amount);
      const sale = parseFloat(priceV2.amount);
      return Math.round(((original - sale) / original) * 100);
    },
    sellingPlanName() {
      return this.line.sellingPlanAllocation?.sellingPlan?.name || false;
    },
    estimatedDeliveryLabel() {
      return getEstimatedDeliveryLabel(this.line);
    },
    shouldShowFinalSale() {
      const { finalSaleEnabled = false } = getFeatureToggles();
      if (!finalSaleEnabled) return false;

      return hasFinalSaleTag(this.line?.merchandise?.product?.tags);
    },
    shipsFromUkWarehouse() {
      return hasUkWarehouseTag(this.line?.merchandise?.product?.tags);
    },
    ukWarehouseDeliveryMessage() {
      return UK_WAREHOUSE_DELIVERY_MESSAGE;
    },
  },
  methods: {
    onQuantityChange(val) {
      this.$emit("updateLine", val);
    },
    openFinalSaleModal() {
      this.finalSaleModalOpen = true;
    },
    closeFinalSaleModal() {
      this.finalSaleModalOpen = false;
    },
    openUkWarehouseModal() {
      this.ukWarehouseModalOpen = true;
    },
    closeUkWarehouseModal() {
      this.ukWarehouseModalOpen = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.product {
  display: flex;
  font-size: 9px;
  font-weight: 400;
  border-bottom: 1px solid rgba($color-grey-dark, 0.2);
  margin: 0 -20px;
  padding: 20px;

  &:last-child {
    border-bottom: none;
  }

  &__image-wrapper {
    flex: 1 0 15%;
    margin-right: 20px;
  }

  &__image {
    max-width: 100%;
    height: auto;
  }

  &__details {
    flex: 1 0 calc(60% - 20px);
    font-family: $utility-font-family;
    font-weight: 400;
  }

  &__title {
    font-family: $heading-font-family;
    font-size: 12px;
    text-transform: none;
    margin-bottom: 10px;
    font-weight: normal;
    letter-spacing: normal;
  }

  &__variant {
    display: flex;
    justify-content: space-between;
    margin-bottom: 5px;
    font-size: 11px;
    letter-spacing: 0.5px;
  }

  &__price {
    margin-bottom: 12px;
    font-size: 11px;
    letter-spacing: 0.5px;

    &--onsale {
      .product__price-original {
        text-decoration: line-through;
        margin-right: 8px;
      }
    }

    &-original {
      margin-right: 8px;
    }

    &-sale {
      color: $color-red;
    }

    &-percent {
      color: $color-red;
    }
  }

  &--has-final-sale &__price {
    margin-bottom: 4px;
  }

  &__quantity {
    display: flex;
    justify-content: space-between;
    padding-bottom: 20px;
  }

  &__sellingPlan-label {
    margin-bottom: 5px;
    font-size: 12px;
  }

  &__estimated-delivery {
    font-family: $heading-font-family;
    font-size: 12px;
    text-transform: none;
    font-weight: normal;
    color: $color-black;
    margin: 4px 0 8px;
  }

  &__uk-warehouse {
    margin-top: 0;
    margin-bottom: 8px;

    .uk-warehouse-trigger {
      @include utility-label(12px);
      color: $color-black;
      background: none;
      border: none;
      padding: 0;
      margin: 0;
      cursor: pointer;
      text-decoration: none;
      display: inline-flex;
      align-items: center;
      gap: 6px;
      font-weight: normal;
      appearance: none;
      -webkit-appearance: none;

      &__label {
        display: inline-block;
        flex: 0 0 auto;
        color: inherit;
        white-space: nowrap;
      }

      &:hover {
        opacity: 0.8;
      }

      .question-mark-icon {
        width: 12px;
        height: 12px;
        flex-shrink: 0;
      }
    }
  }

  &__final-sale {
    margin-top: 0;
    margin-bottom: 8px;

    .final-sale-trigger {
      @include utility-label(12px);
      color: $color-red;
      background: none;
      border: none;
      padding: 0;
      margin: 0;
      cursor: pointer;
      text-decoration: none;
      display: inline-flex;
      align-items: center;
      gap: 6px;
      font-weight: normal;
      appearance: none;
      -webkit-appearance: none;

      &__label {
        display: inline-block;
        flex: 0 0 auto;
        color: inherit;
        white-space: nowrap;
      }

      &:hover {
        opacity: 0.8;
      }

      .question-mark-icon {
        width: 12px;
        height: 12px;
        flex-shrink: 0;
      }
    }
  }
}

.edit-variant-button,
.remove-product-button {
  outline: none;
  background: none;
  border: none;
  font-size: 11px;
  font-family: $utility-font-family;
  font-weight: 400;
  letter-spacing: 0.5px;
  text-transform: uppercase;
}

.final-sale-modal-content {
  text-align: left;

  p {
    @include utility-body(16px);
    color: $color-black;
    line-height: 1.5;
    margin: 0;
  }
}
</style>
